import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.postertopia.se';
const DEFAULT_IMAGE = `${SITE_URL}/logo512.png`;
const SITE_NAME = 'PosterTopia';

const SEO = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  noindex = false,
  jsonLd = null,
}) => {
  const fullTitle = title
    ? `${title} | ${SITE_NAME}`
    : `${SITE_NAME} - Unika Posters och Konsttryck Online`;

  const defaultDescription =
    'Upptäck PosterTopias breda utbud av stilfulla och prisvärda posters. Från konstnärliga tryck till trendiga designer, hitta den perfekta inredningen för ditt utrymme. Snabb leverans i Sverige.';

  const metaDescription = description || defaultDescription;
  const canonicalUrl = `${SITE_URL}${path}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={canonicalUrl} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={image} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="sv_SE" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
};

// Organization schema for the whole site
export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: 'PosterTopia',
  url: 'https://www.postertopia.se',
  logo: 'https://www.postertopia.se/logo512.png',
  description:
    'Unika posters och konsttryck online. Stilfulla och prisvärda posters med snabb leverans i Sverige.',
  address: {
    '@type': 'PostalAddress',
    addressCountry: 'SE',
  },
};

// WebSite schema with search action for sitelinks search box
export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'PosterTopia',
  url: 'https://www.postertopia.se',
  potentialAction: {
    '@type': 'SearchAction',
    target: 'https://www.postertopia.se/search/{search_term_string}',
    'query-input': 'required name=search_term_string',
  },
};

// Helper to generate product schema for individual posters
export const createProductSchema = ({ name, description, image, url, price, category }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image,
  url,
  category,
  brand: {
    '@type': 'Brand',
    name: 'PosterTopia',
  },
  offers: {
    '@type': 'Offer',
    priceCurrency: 'SEK',
    price,
    availability: 'https://schema.org/InStock',
    seller: {
      '@type': 'Organization',
      name: 'PosterTopia',
    },
  },
});

// Helper to generate BreadcrumbList schema
export const createBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `https://www.postertopia.se${item.path}`,
  })),
});

// Helper for collection/category pages
export const createCollectionSchema = ({ name, description, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name,
  description,
  url,
  isPartOf: {
    '@type': 'WebSite',
    name: 'PosterTopia',
    url: 'https://www.postertopia.se',
  },
});

export default SEO;
